/**
 * @module   public/js/services/dialers/zoomPhoneDialer.js
 * @summary  Zoom Phone deeplink-based dialer implementation.
 *
 * @description
 * Triggers Zoom Phone calls via `zoomphonecall://` deeplinks while seeding a
 * CallLogs intent through the backend. Uses the same BaseDialer contract as
 * Twilio so Focus Mode can switch providers without schema changes. Backend
 * intent logging uses existing CallLogs fields (`calledVia`, `origin`, etc.)
 * and never alters outcome schemas.
 */

import BaseDialer from './baseDialer.js';
import { auth } from '../../core/firebase-service.js';

/**
 * Build a Zoom Phone deeplink URL.
 *
 * @param {object} params
 * @param {string} params.to - E.164 destination number.
 * @param {string=} params.callerId - Optional caller ID.
 * @param {string=} params.extension - Optional phone extension metadata.
 * @param {string=} params.correlationId - Call intent identifier for webhook correlation.
 * @returns {string} Fully composed deeplink URL.
 */
function buildZoomDeeplink({ to, callerId, extension, correlationId } = {}) {
  const base = `zoomphonecall://${encodeURIComponent(to)}`;
  const query = new URLSearchParams();
  if (callerId) query.set('callerid', callerId);
  if (extension) query.set('extension', extension);
  if (correlationId) query.set('zcid', correlationId);
  const qs = query.toString();
  return qs ? `${base}?${qs}` : base;
}

export default class ZoomPhoneDialer extends BaseDialer {
  /**
   * @param {{ apiClient:Object, endpoints:Object }} deps
   */
  constructor({ apiClient, endpoints } = {}) {
    super();
    if (!apiClient || !endpoints) {
      throw new Error('ZoomPhoneDialer needs { apiClient, endpoints }');
    }
    this.apiClient = apiClient;
    this.endpoints = endpoints;
  }

  /**
   * Launch a Zoom Phone call via deeplink while logging call intent.
   *
   * @param {object} opts
   * @param {string} opts.to - E.164 dial target.
   * @param {string=} opts.extension - Optional phone extension.
   * @param {string=} opts.contactId - CRM contact identifier.
   * @param {string=} opts.userId - Explicit user identifier.
   * @param {string=} opts.taskId - Focus Mode task identifier.
   * @param {string=} opts.fromNumber - Optional caller ID.
   * @returns {Promise<{ callSid: string|null }>} Call intent identifier.
   */
  async call({
    to,
    extension = '',
    contactId = '',
    userId = '',
    taskId = '',
    fromNumber = '',
  } = {}) {
    if (!to) throw new Error('ZoomPhoneDialer.call → missing `to` number');

    const uid =
      userId ||
      auth?.currentUser?.uid ||
      this.apiClient?.auth?.currentUser?.uid ||
      window?.di?.auth?.currentUser?.uid ||
      null;

    const tenantId =
      auth?.currentUser?.tenantId ||
      this.apiClient?.auth?.currentUser?.tenantId ||
      window?.APP_CONFIG?.tenantId ||
      window?.di?.auth?.currentUser?.tenantId ||
      null;

    const payload = {
      toNumber: to,
      extension: extension || undefined,
      contactId: contactId || undefined,
      userId: uid || undefined,
      taskId: taskId || undefined,
    };
    if (fromNumber) payload.fromNumber = fromNumber;
    if (tenantId) payload.tenantId = tenantId;

    let callSid = null;
    try {
      const url =
        typeof this.endpoints.zoomCallInitiate === 'function'
          ? this.endpoints.zoomCallInitiate({})
          : this.endpoints.zoomCallInitiate;
      const res = await this.apiClient.fetchJson(url, {
        auth: true,
        body: payload,
      });
      if (res?.ok === false) {
        throw new Error(
          res?.error || 'ZoomPhoneDialer: backend reported failure'
        );
      }
      callSid =
        res?.callSid ||
        res?.data?.callSid ||
        res?.data?.sid ||
        res?.sid ||
        null;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[ZoomPhoneDialer] intent logging failed', err);
    }

    try {
      const deeplink = buildZoomDeeplink({
        to,
        callerId: fromNumber || undefined,
        extension: extension || undefined,
        correlationId: callSid || undefined,
      });
      window.location.href = deeplink;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[ZoomPhoneDialer] deeplink launch failed', err);
    }

    return { callSid };
  }
}

export { buildZoomDeeplink };
